import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import NfcIcon from '@mui/icons-material/Nfc';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import MoneyIcon from '@mui/icons-material/Money';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { cardManagementService } from '../../../../../services/cardManagementService';
import { useAuth } from '../../../../../contexts/AuthContext';

export interface ServiceItemForModal {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

export interface EntitlementForModal {
  productId: string;
  productName: string;
  quantity: number;
  unitPricePaid: number;
}

export interface CardSlotForModal {
  serviceItem?: ServiceItemForModal;
  packageName?: string;
  entitlements?: EntitlementForModal[];
}

export type DepositPaymentMethod = 'CASH' | 'POS';
type DepositPaymentMethodState = DepositPaymentMethod | null;

export interface ScannedCardData {
  cardUID: string;
  cardId?: number;
  cardLabel: string;
  cardTypeName?: string;
  depositAmount: number;
  entryRights: number;
  assignedServiceItem?: ServiceItemForModal;
  packageName?: string;
  entitlements?: EntitlementForModal[];
  /** Bu karta atanan kart slotu (cardSlots index'i). Operatör elle değiştirebilir. */
  assignedSlotIndex?: number;
}

interface CardTypeInfo {
  id: number;
  name: string;
  depositAmount: number;
  color?: string;
}

interface PrePaymentCardScanModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (cards: ScannedCardData[], depositPaymentMethod: DepositPaymentMethod) => void;
  /**
   * Verilirse "Kartsız Devam Et" butonu gösterilir. Kasiyer bu işlemde kart vermek
   * istemiyorsa (örn. toplu müşteri) kart okutmadan ödemeye geçer; depozito alınmaz,
   * kart atanmaz — işlem normal hızlı satış gibi tamamlanır.
   */
  onSkip?: () => void;
  cardMode: 'ONE_PER_RIGHT' | 'FLEXIBLE';
  serviceItems: ServiceItemForModal[];
  /**
   * Kulüp girişi paket-instance kart slotları. Verilirse her kart sırasıyla bir slota atanır;
   * slotun serviceItem'ı (giriş hakkı) + entitlements'ı (karta yazılacak ön ödemeli ürün hakları)
   * okutulan karta bağlanır. ONE_PER_RIGHT modunda kart sayısı = slot sayısı.
   */
  cardSlots?: CardSlotForModal[];
  depositEnabled: boolean;
  servicePaymentMethod?: 'CASH' | 'POS';
  /**
   * Rezervasyon "Giriş Yap" akışı için: bilet ücretleri QR menüden zaten alındı.
   * true iken UI'da fiyatlar gizlenir, "Online Ödendi" gösterilir; deposito sorulmaya devam eder.
   */
  prepaidMode?: boolean;
  /**
   * Rezervasyon "Giriş Yap" akışı için: kart sayısı serviceItems üzerinden değil,
   * dışarıdan (örn. guestCount) belirlenir. Verilmezse sum(serviceItems.quantity).
   */
  expectedCardCountOverride?: number;
}

const PrePaymentCardScanModal: React.FC<PrePaymentCardScanModalProps> = ({
  open,
  onClose,
  onConfirm,
  onSkip,
  cardMode,
  serviceItems,
  cardSlots,
  depositEnabled,
  servicePaymentMethod,
  prepaidMode = false,
  expectedCardCountOverride,
}) => {
  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId;
  const [scannedCards, setScannedCards] = useState<ScannedCardData[]>([]);
  const [cardInput, setCardInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardTypes, setCardTypes] = useState<CardTypeInfo[]>([]);
  const [depositPaymentMethod, setDepositPaymentMethod] = useState<DepositPaymentMethodState>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const slotCount = cardSlots?.length || 0;
  const computedFromItems = serviceItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalEntryRights = (expectedCardCountOverride && expectedCardCountOverride > 0)
    ? expectedCardCountOverride
    : (slotCount > 0 ? slotCount : computedFromItems);
  const assignedRights = scannedCards.reduce((sum, c) => sum + c.entryRights, 0);
  const remainingRights = totalEntryRights - assignedRights;

  const isOnePerRight = cardMode === 'ONE_PER_RIGHT';
  const expectedCardCount = isOnePerRight ? totalEntryRights : undefined;

  useEffect(() => {
    if (open) {
      setScannedCards([]);
      setCardInput('');
      setError(null);
      setScanning(false);
      setDepositPaymentMethod(null);

      if (depositEnabled && companyId) {
        console.log('🏷️ [PrePaymentCardScan] Kart tipleri yükleniyor... companyId:', companyId);
        cardManagementService.getCardTypes(companyId).then(res => {
          console.log('🏷️ [PrePaymentCardScan] API raw response:', res.data);
          const rawData = res.data?.data || res.data || [];
          const types = rawData.map((t: any) => ({
            id: t.id || t.Id,
            name: t.name || t.Name,
            depositAmount: t.depositAmount ?? t.DepositAmount ?? 0,
            color: t.color || t.Color,
          }));
          console.log('🏷️ [PrePaymentCardScan] Parse edilen kart tipleri:', types.map((ct: CardTypeInfo) => `${ct.name}: ${ct.depositAmount}₺`));
          setCardTypes(types);
        }).catch(err => {
          console.error('❌ [PrePaymentCardScan] Kart tipleri yüklenemedi:', err);
        });
      } else {
        console.log('🏷️ [PrePaymentCardScan] Deposito devre dışı veya companyId yok:', { depositEnabled, companyId });
      }

      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open, depositEnabled, companyId]);

  const resolveCardDeposit = useCallback(async (uid: string): Promise<{ depositAmt: number; typeName?: string; cardId?: number; notFound?: boolean; isMasterCard?: boolean }> => {
    console.log('🔍 [resolveCardDeposit] Başlatılıyor:', { uid, depositEnabled, companyId, cardTypesCount: cardTypes.length });
    if (!companyId) { return { depositAmt: 0, notFound: true }; }

    try {
      const res = await cardManagementService.getCardByUID(companyId, uid);
      const card = res.data?.data || res.data;
      if (card) {
        if (card.isMasterCard || card.IsMasterCard) {
          return { depositAmt: 0, isMasterCard: true };
        }

        const cardTypeId = card.cardTypeId || card.CardTypeId;
        const resolvedCardId = card.id || card.Id;

        if (!depositEnabled) {
          return { depositAmt: 0, cardId: resolvedCardId };
        }

        const matchedType = cardTypes.find(t => t.id === cardTypeId);
        if (matchedType && matchedType.depositAmount > 0) {
          return { depositAmt: matchedType.depositAmount, typeName: matchedType.name, cardId: resolvedCardId };
        }
        const defaultType = cardTypes.length > 0 ? cardTypes[0] : null;
        if (defaultType && defaultType.depositAmount > 0) {
          return { depositAmt: defaultType.depositAmount, typeName: defaultType.name, cardId: resolvedCardId };
        }
        return { depositAmt: 0, cardId: resolvedCardId };
      }
    } catch (err) {
      console.log('⚠️ [resolveCardDeposit] Kart sistemde bulunamadı:', err);
    }

    return { depositAmt: 0, notFound: true };
  }, [depositEnabled, companyId, cardTypes]);

  const handleScanCard = useCallback(async () => {
    const uid = cardInput.trim();
    if (!uid) return;

    if (scannedCards.some(c => c.cardUID === uid)) {
      setError('Bu kart zaten okutuldu');
      return;
    }

    if (isOnePerRight && scannedCards.length >= totalEntryRights) {
      setError(`Maksimum ${totalEntryRights} kart okutulabilir`);
      return;
    }

    if (remainingRights <= 0) {
      setError('Tüm geçiş hakları atanmış durumda');
      return;
    }

    setScanning(true);
    setError(null);

    try {
      const { depositAmt, typeName, cardId, notFound, isMasterCard } = await resolveCardDeposit(uid);

      if (isMasterCard) {
        setError('Bu bir MASTER karttır! Master kartlar müşteriye verilemez, sadece yetkili personel tarafından geçiş için kullanılır.');
        setScanning(false);
        setTimeout(() => inputRef.current?.focus(), 100);
        return;
      }

      if (notFound) {
        setError('Bu kart sistemde kayıtlı değil. Lütfen önce Kart Yönetimi bölümünden kartı tanımlayın.');
        setScanning(false);
        setTimeout(() => inputRef.current?.focus(), 100);
        return;
      }

      if (cardId && companyId) {
        try {
          const sessionRes = await cardManagementService.getActiveSession(companyId, cardId);
          if (sessionRes?.data?.data?.id || sessionRes?.data?.hasActiveSession) {
            setError('Aktif oturumu olan kart tespit edildi! Bu kart şu anda kullanımda olduğu için yeniden verilemez. Lütfen önce kartın oturumunu sonlandırın.');
            setScanning(false);
            setTimeout(() => inputRef.current?.focus(), 100);
            return;
          }
        } catch (_) { /* aktif oturum yok, devam et */ }
      }

      console.log('🎯 [handleScanCard] Kart ekleniyor:', { uid, depositAmt, typeName, cardId });

      const newCard: ScannedCardData = {
        cardUID: uid,
        cardId,
        cardLabel: `Kart ${scannedCards.length + 1}`,
        cardTypeName: typeName,
        depositAmount: depositAmt,
        entryRights: isOnePerRight ? 1 : 1,
      };

      if (isOnePerRight) {
        // ✅ Paket slotları varsa: İLK ATANMAMIŞ slotu bu karta ata (giriş hizmeti +
        // entitlement'lar). Operatör sonradan dropdown'dan hangi bilekliğe hangi
        // paketin gideceğini değiştirebilir (karışık menülü/menüsüz paket senaryosu).
        if (cardSlots && cardSlots.length > 0) {
          const usedIdx = new Set(
            scannedCards.map(c => c.assignedSlotIndex).filter((i): i is number => i != null)
          );
          let slotIndex = cardSlots.findIndex((_, i) => !usedIdx.has(i));
          if (slotIndex < 0) slotIndex = scannedCards.length;
          const slot = cardSlots[slotIndex];
          if (slot) {
            newCard.assignedSlotIndex = slotIndex;
            newCard.assignedServiceItem = slot.serviceItem;
            newCard.entitlements = slot.entitlements || [];
            newCard.packageName = slot.packageName;
          }
        } else {
          let assigned = false;
          for (const si of serviceItems) {
            const alreadyAssignedCount = scannedCards.filter(
              sc => sc.assignedServiceItem?.id === si.id
            ).length;
            if (alreadyAssignedCount < si.quantity) {
              newCard.assignedServiceItem = si;
              assigned = true;
              break;
            }
          }
          if (!assigned && serviceItems.length > 0) {
            newCard.assignedServiceItem = serviceItems[0];
          }
        }
      }

      setScannedCards(prev => [...prev, newCard]);
      setCardInput('');
    } catch (err) {
      setError('Kart okutulurken hata oluştu');
    } finally {
      setScanning(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [cardInput, scannedCards, isOnePerRight, totalEntryRights, remainingRights, resolveCardDeposit, serviceItems, cardSlots]);

  const handleRemoveCard = (uid: string) => {
    setScannedCards(prev => prev.filter(c => c.cardUID !== uid));
    setError(null);
  };

  const handleUpdateRights = (uid: string, rights: number) => {
    setScannedCards(prev =>
      prev.map(c => (c.cardUID === uid ? { ...c, entryRights: rights } : c))
    );
  };

  // Bir slotu okutulan kart listesinde göstermek için etiket (paket adı + ürün hakları).
  const slotLabel = useCallback((slot: CardSlotForModal, index: number) => {
    const ents = (slot.entitlements || [])
      .map(e => `${e.productName}${e.quantity > 1 ? ` x${e.quantity}` : ''}`)
      .join(', ');
    const base = slot.packageName || slot.serviceItem?.name || `Paket ${index + 1}`;
    return ents ? `${base} → 🎁 ${ents}` : `${base} (ürün yok)`;
  }, []);

  // Operatör bir bilekliğe atanacak paketi/slotu değiştirir.
  // Hedef slot başka bir kartta ise iki kart arasında takas (swap) yapılır;
  // böylece her slot tam olarak bir karta atanmış kalır (aynı isimli paketlerde de).
  const handleChangeSlot = useCallback((uid: string, newSlotIndex: number) => {
    setScannedCards(prev => {
      const current = prev.find(c => c.cardUID === uid);
      const prevIdx = current?.assignedSlotIndex;
      const applySlot = (c: ScannedCardData, idx: number): ScannedCardData => {
        const slot = cardSlots?.[idx];
        return {
          ...c,
          assignedSlotIndex: idx,
          assignedServiceItem: slot?.serviceItem,
          entitlements: slot?.entitlements || [],
          packageName: slot?.packageName,
        };
      };
      return prev.map(c => {
        if (c.cardUID === uid) return applySlot(c, newSlotIndex);
        if (c.assignedSlotIndex === newSlotIndex && prevIdx != null) return applySlot(c, prevIdx);
        return c;
      });
    });
  }, [cardSlots]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScanCard();
    }
  };

  const canConfirm = () => {
    if (scannedCards.length === 0) return false;
    const totalDeposit = scannedCards.reduce((sum, c) => sum + c.depositAmount, 0);
    if (depositEnabled && totalDeposit > 0 && !depositPaymentMethod) return false;
    if (isOnePerRight) {
      if (scannedCards.length !== totalEntryRights) return false;
      // Paket slotları varsa: her kartın bir slotu olmalı ve slotlar benzersiz olmalı.
      if (cardSlots && cardSlots.length > 0) {
        const idxs = scannedCards.map(c => c.assignedSlotIndex);
        if (idxs.some(i => i == null)) return false;
        if (new Set(idxs).size !== idxs.length) return false;
      }
      return true;
    }
    return assignedRights === totalEntryRights;
  };

  const handleConfirm = () => {
    if (!canConfirm()) return;
    onConfirm(scannedCards, depositPaymentMethod || 'CASH');
  };

  const totalDeposit = scannedCards.reduce((sum, c) => sum + c.depositAmount, 0);

  const getFlexRightsOptions = () => {
    const maxPerCard = remainingRights;
    const options = [];
    for (let i = 1; i <= Math.min(maxPerCard, totalEntryRights); i++) {
      options.push(i);
    }
    return options;
  };

  const serviceTotal = serviceItems.reduce((sum, si) => sum + si.price * si.quantity, 0);
  // Kulüp girişi: pakete dahil ürünler (entitlement) girişte normal fiyatından tahsil edilir.
  // Ürünler kart slotlarında entitlement olarak taşınır; tahsil edilecek toplama dahil edilmeli.
  // (prepaidMode = ön ödemeli istasyon; orada ürünler 0 ₺ kullanılır, toplama eklenmez.)
  const serviceItemIds = new Set(serviceItems.map((si) => si.id));
  const productsTotal = prepaidMode
    ? 0
    : (cardSlots || []).reduce(
        (sum, slot) =>
          sum +
          (slot.entitlements || []).reduce(
            // serviceItems zaten ürünleri içeriyorsa (hizmet yok senaryosu) çift saymayı önle
            (s, e) =>
              serviceItemIds.has(e.productId)
                ? s
                : s + (e.unitPricePaid || 0) * (e.quantity || 1),
            0
          ),
        0
      );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NfcIcon color="primary" />
          <Typography variant="h6">Kart Okutma</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`Kalan Hak: ${remainingRights}`}
            color={remainingRights === 0 ? 'success' : 'warning'}
            size="small"
          />
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {/* Kart okutma formu - tam genişlik üstte */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            label="Kart UID okutun veya girin"
            value={cardInput}
            onChange={(e) => { setCardInput(e.target.value); setError(null); }}
            onKeyDown={handleKeyDown}
            disabled={scanning || (isOnePerRight && scannedCards.length >= totalEntryRights) || remainingRights <= 0}
            autoFocus
            placeholder={isOnePerRight
              ? `${totalEntryRights} kart okutmanız gerekiyor`
              : `Toplam ${totalEntryRights} geçiş hakkı dağıtın`}
          />
          <Button
            variant="contained"
            onClick={handleScanCard}
            disabled={scanning || !cardInput.trim() || (isOnePerRight && scannedCards.length >= totalEntryRights) || remainingRights <= 0}
            sx={{ minWidth: 80 }}
          >
            {scanning ? <CircularProgress size={20} /> : 'Ekle'}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* İki sütunlu layout */}
        <Grid container spacing={2}>
          {/* SOL SÜTUN: Okutulan kartlar */}
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 1.5, height: '100%', minHeight: 250 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon fontSize="small" color="primary" />
                Okutulan Kartlar ({scannedCards.length}{isOnePerRight ? `/${totalEntryRights}` : ''})
              </Typography>
              <Divider sx={{ mb: 1 }} />

              {scannedCards.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <NfcIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Kart okutmak için yukarıdaki alana UID girin
                  </Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {scannedCards.map((card, index) => (
                    <React.Fragment key={card.cardUID}>
                      <ListItem
                        disableGutters
                        secondaryAction={
                          <IconButton edge="end" onClick={() => handleRemoveCard(card.cardUID)} size="small">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                        sx={{ py: 0.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CreditCardIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          secondaryTypographyProps={{ component: 'div' } as any}
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                                {card.cardUID}
                              </Typography>
                              {card.cardTypeName && (
                                <Chip label={card.cardTypeName} size="small" variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }} />
                              )}
                              {depositEnabled && card.depositAmount > 0 && (
                                <Chip
                                  label={`${card.depositAmount.toFixed(0)} ₺`}
                                  size="small"
                                  color="warning"
                                  sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            isOnePerRight ? (
                              cardSlots && cardSlots.length > 0 ? (
                                // ✅ Operatör hangi bilekliğe hangi paketi/ürünü atayacağını seçer.
                                <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                                  <FormControl size="small" fullWidth sx={{ maxWidth: 340 }}>
                                    <Select
                                      value={card.assignedSlotIndex ?? ''}
                                      onChange={(e) => handleChangeSlot(card.cardUID, Number(e.target.value))}
                                      size="small"
                                      displayEmpty
                                      sx={{ fontSize: '0.78rem', '& .MuiSelect-select': { py: 0.5 } }}
                                    >
                                      {cardSlots.map((slot, i) => (
                                        <MenuItem key={i} value={i} sx={{ fontSize: '0.78rem', whiteSpace: 'normal' }}>
                                          {slotLabel(slot, i)}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Box>
                              ) : (
                              <Box component="span" sx={{ display: 'block' }}>
                                <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                                  {card.packageName ? `${card.packageName}: ` : ''}{card.assignedServiceItem?.name} → 1 geçiş hakkı
                                </Typography>
                                {card.entitlements && card.entitlements.length > 0 && (
                                  <Typography variant="caption" color="success.main" component="span" sx={{ display: 'block' }}>
                                    🎁 {card.entitlements.map(e => `${e.productName}${e.quantity > 1 ? ` x${e.quantity}` : ''}`).join(', ')}
                                  </Typography>
                                )}
                              </Box>
                              )
                            ) : (
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <Select
                                    value={card.entryRights}
                                    onChange={(e) => handleUpdateRights(card.cardUID, Number(e.target.value))}
                                    size="small"
                                    sx={{ height: 28, fontSize: '0.8rem' }}
                                  >
                                    {Array.from(
                                      { length: Math.min(remainingRights + card.entryRights, totalEntryRights) },
                                      (_, i) => i + 1
                                    ).map(n => (
                                      <MenuItem key={n} value={n}>{n} hak</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            )
                          }
                        />
                      </ListItem>
                      {index < scannedCards.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          {/* SAĞ SÜTUN: Özet bilgileri */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%' }}>
              {/* Sepetteki Hizmetler */}
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    Hizmet Kalemleri
                  </Typography>
                  {prepaidMode ? (
                    <Chip
                      label="Online Ödendi"
                      size="small"
                      color="success"
                      variant="filled"
                      sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem', fontWeight: 700 } }}
                    />
                  ) : servicePaymentMethod && (
                    <Chip
                      icon={servicePaymentMethod === 'POS' ? <PaymentIcon /> : <MoneyIcon />}
                      label={servicePaymentMethod === 'POS' ? 'POS' : 'Nakit'}
                      size="small"
                      color={servicePaymentMethod === 'POS' ? 'info' : 'success'}
                      variant="outlined"
                      sx={{ height: 22, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                    />
                  )}
                </Box>
                {serviceItems.map(si => (
                  <Box key={si.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {si.name}{prepaidMode ? ` × ${si.quantity}` : ''}
                    </Typography>
                    {prepaidMode ? (
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem', color: 'success.main' }}>
                        Ödendi
                      </Typography>
                    ) : (
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                        {si.quantity} x {si.price.toFixed(2)} ₺
                      </Typography>
                    )}
                  </Box>
                ))}
                <Divider sx={{ my: 0.5 }} />
                {prepaidMode ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="bold">Bilet Ücreti</Typography>
                    <Chip
                      label="Online Ödendi"
                      size="small"
                      color="success"
                      variant="filled"
                      sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem', fontWeight: 700 } }}
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" fontWeight="bold">Hizmet Toplamı</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {serviceTotal.toFixed(2)} ₺
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Deposito Özeti + Ödeme Yöntemi Seçimi */}
              {depositEnabled && (
                <Paper variant="outlined" sx={{ p: 1.5, borderColor: totalDeposit > 0 ? 'warning.main' : undefined }}>
                  <Typography variant="subtitle2" color="warning.main" gutterBottom sx={{ fontSize: '0.8rem' }}>
                    Deposito
                  </Typography>
                  {scannedCards.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      Kart okutulduğunda deposito hesaplanır
                    </Typography>
                  ) : (
                    <>
                      {scannedCards.map(card => (
                        <Box key={card.cardUID} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography variant="caption" color="text.secondary">
                            {card.cardUID.slice(-6)} ({card.cardTypeName || '-'})
                          </Typography>
                          <Typography variant="caption" fontWeight="bold" color="warning.main">
                            +{card.depositAmount.toFixed(2)} ₺
                          </Typography>
                        </Box>
                      ))}
                      <Divider sx={{ my: 0.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="bold">Toplam Deposito</Typography>
                        <Typography variant="body2" fontWeight="bold" color="warning.main">
                          +{totalDeposit.toFixed(2)} ₺
                        </Typography>
                      </Box>

                      {/* Deposito ödeme yöntemi seçimi */}
                      {totalDeposit > 0 && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                            Deposito ödeme yöntemi:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant={depositPaymentMethod === 'CASH' ? 'contained' : 'outlined'}
                              size="small"
                              startIcon={<MoneyIcon />}
                              onClick={() => setDepositPaymentMethod('CASH')}
                              color="success"
                              sx={{ flex: 1, textTransform: 'none', fontSize: '0.75rem' }}
                            >
                              Nakit
                            </Button>
                            <Button
                              variant={depositPaymentMethod === 'POS' ? 'contained' : 'outlined'}
                              size="small"
                              startIcon={<PaymentIcon />}
                              onClick={() => setDepositPaymentMethod('POS')}
                              color="info"
                              sx={{ flex: 1, textTransform: 'none', fontSize: '0.75rem' }}
                            >
                              POS
                            </Button>
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </Paper>
              )}

              {/* Uyarı mesajları */}
              {depositEnabled && totalDeposit > 0 && depositPaymentMethod === 'CASH' && (
                <Alert severity="info" icon={<InfoOutlinedIcon />} variant="outlined" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                  Deposito nakit olarak alınacak, yazarkasadan geçmeyecektir.
                </Alert>
              )}
              {depositEnabled && totalDeposit > 0 && !depositPaymentMethod && (
                <Alert severity="warning" icon={<WarningAmberIcon />} variant="outlined" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                  Lütfen deposito için ödeme yöntemi seçiniz.
                </Alert>
              )}

              {/* Ödenecek Toplam (prepaidMode'da bilet ücreti gösterilmez, yalnızca deposito) */}
              <Paper
                elevation={3}
                sx={{
                  p: 1.5,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  mt: 'auto',
                }}
              >
                {prepaidMode ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Bilet
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">Online Ödendi</Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Hizmet ({servicePaymentMethod === 'POS' ? 'POS' : 'Nakit'})
                      </Typography>
                      <Typography variant="body2">{serviceTotal.toFixed(2)} ₺</Typography>
                    </Box>
                    {productsTotal > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Ürünler
                        </Typography>
                        <Typography variant="body2">+{productsTotal.toFixed(2)} ₺</Typography>
                      </Box>
                    )}
                  </>
                )}
                {depositEnabled && totalDeposit > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Deposito ({depositPaymentMethod === 'POS' ? 'POS' : depositPaymentMethod === 'CASH' ? 'Nakit' : 'Seçilmedi'})
                    </Typography>
                    <Typography variant="body2">+{totalDeposit.toFixed(2)} ₺</Typography>
                  </Box>
                )}
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.3)', my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight="bold">
                    {prepaidMode ? 'TAHSİL EDİLECEK' : 'TOPLAM'}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {(prepaidMode ? totalDeposit : serviceTotal + productsTotal + totalDeposit).toFixed(2)} ₺
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          İptal
        </Button>
        {onSkip && !prepaidMode && (
          <Button
            onClick={onSkip}
            color="warning"
            variant="outlined"
            sx={{ mr: 'auto' }}
          >
            Kartsız Devam Et
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm()}
          startIcon={<CheckCircleIcon />}
          color="success"
          size="large"
        >
          {prepaidMode
            ? (totalDeposit > 0
                ? `Girişi Tamamla (Deposito ${totalDeposit.toFixed(2)} ₺)`
                : 'Girişi Tamamla')
            : `Onayla ve Ödemeye Geç (${(serviceTotal + productsTotal + totalDeposit).toFixed(2)} ₺)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrePaymentCardScanModal;
